import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import TabBuscadorBaterias from './TabBuscadorBaterias';

// ─── Constantes ───────────────────────────────────────────────────────────────
const ESTADOS = {
  activa:      { label: 'Activa',      color: '#4cc9f0', bg: '#001a2a' },
  en_proceso:  { label: 'En proceso',  color: '#ffc107', bg: '#2a1e00' },
  repuesta:    { label: 'Repuesta',    color: '#4caf50', bg: '#0d2218' },
  negada:      { label: 'Negada',      color: '#e63946', bg: '#2a0d0d' },
  vencida:     { label: 'Vencida',     color: '#555',    bg: '#1a1a1a' },
};

const SEMAFORO = {
  verde:    { color: '#4caf50', bg: '#0d2218', icon: '🟢' },
  amarillo: { color: '#ffc107', bg: '#2a1e00', icon: '🟡' },
  rojo:     { color: '#e63946', bg: '#2a0d0d', icon: '🔴' },
};

function fmt(n) { return Number(n || 0).toLocaleString('es-EC'); }
function fmtFecha(s) {
  if (!s) return '—';
  const [y,m,d] = s.slice(0,10).split('-');
  return `${d}/${m}/${y}`;
}
function diasLabel(n) {
  if (n <= 0) return 'Vence hoy';
  if (n === 1) return '1 día';
  return `${n} días`;
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ label, value, color, icon, sub }) {
  return (
    <div style={{ background: '#1e1e2e', borderRadius: 12,
      padding: '16px 20px', borderTop: `3px solid ${color}`,
      display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ color: '#555', fontSize: 11, textTransform: 'uppercase',
        letterSpacing: 1 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <span style={{ color, fontFamily: 'monospace',
          fontWeight: 700, fontSize: 26 }}>{value}</span>
      </div>
      {sub && <div style={{ color: '#555', fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

// ─── Semáforo Bar ─────────────────────────────────────────────────────────────
function SemaforoBarra({ pct_tiempo, pct_km, color }) {
  const pct = Math.max(pct_tiempo, pct_km);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ flex: 1, height: 5, background: '#2a2a3e',
        borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%',
          background: color, borderRadius: 3, transition: 'width .4s' }} />
      </div>
      <span style={{ color: '#555', fontSize: 10, minWidth: 28,
        fontFamily: 'monospace' }}>{Math.round(pct)}%</span>
    </div>
  );
}

// ─── Badge estado ─────────────────────────────────────────────────────────────
function BadgeEstado({ estado }) {
  const e = ESTADOS[estado] || ESTADOS.activa;
  return (
    <span style={{ background: e.bg, color: e.color,
      border: `1px solid ${e.color}44`, borderRadius: 5,
      padding: '2px 8px', fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      {e.label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Modal Registrar Garantía
// ═══════════════════════════════════════════════════════════════════════════════
function ModalRegistrar({ onClose, onGuardado }) {
  const [paso,     setPaso]     = useState(1); // 1=buscar, 2=confirmar, 3=km
  const [query,    setQuery]    = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultado,setResultado]= useState(null);
  const [error,    setError]    = useState('');
  const [bateriaIdx, setBateriaIdx] = useState(0);
  const [kmInicial,  setKmInicial]  = useState('');
  const [kmGarantia, setKmGarantia] = useState(40000);
  const [notas,      setNotas]      = useState('');
  const [guardando,  setGuardando]  = useState(false);

  async function buscar() {
    if (!query.trim()) return;
    setBuscando(true); setError(''); setResultado(null);
    try {
      const d = await api.get(`/garantias/buscar-factura?q=${encodeURIComponent(query.trim())}`);
      if (d.error) { setError(d.error); }
      else if (!d.encontrada) { setError(d.mensaje); }
      else if (!d.tiene_baterias) { setError(d.mensaje); }
      else {
        setResultado(d);
        if (d.km_obs) setKmInicial(String(d.km_obs));
        if (d.baterias?.[0]) setKmGarantia(d.baterias[0].km_garantia);
        setPaso(2);
      }
    } catch { setError('Error de conexión'); }
    setBuscando(false);
  }

  async function guardar() {
    if (!resultado) return;
    const bat = resultado.baterias[bateriaIdx];
    setGuardando(true);
    try {
      const d = await api.post('/garantias/baterias', {
        factura:       resultado.factura,
        facid:         resultado.facid,
        fecha_venta:   resultado.fecha_venta,
        item_id:       bat.item_id,
        descripcion:   bat.descripcion,
        precio_venta:  bat.precio_venta,
        cedula:        resultado.cedula,
        nombre_cliente:resultado.cliente,
        km_inicial:    kmInicial ? parseInt(kmInicial) : null,
        km_garantia:   kmGarantia,
        km_inicial_raw:resultado.obs_text || '',
        vendedor:      resultado.vendedor,
        notas,
        registrado_por:'panel',
      });
      if (d.ok) { onGuardado(); onClose(); }
      else { setError(d.error || 'Error al guardar'); }
    } catch { setError('Error de conexión'); }
    setGuardando(false);
  }

  const bat = resultado?.baterias?.[bateriaIdx];

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0,
        background:'rgba(0,0,0,0.65)', zIndex:200 }} />
      <div style={{
        position:'fixed', top:'50%', left:'50%',
        transform:'translate(-50%,-50%)',
        background:'#1e1e2e', borderRadius:14, padding:28,
        width:520, zIndex:201, border:'1px solid #2a2a3e',
        boxShadow:'0 16px 48px rgba(0,0,0,0.6)',
        display:'flex', flexDirection:'column', gap:20,
      }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between',
          alignItems:'center' }}>
          <div>
            <div style={{ color:'#4cc9f0', fontWeight:700, fontSize:16 }}>
              🔋 Registrar Garantía
            </div>
            <div style={{ color:'#555', fontSize:12, marginTop:2 }}>
              {paso === 1 ? 'Buscar factura de venta' :
               paso === 2 ? 'Confirmar batería' : 'Kilometraje inicial'}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none',
            color:'#555', fontSize:20, cursor:'pointer' }}>✕</button>
        </div>

        {/* Paso 1 — Buscar factura */}
        {paso === 1 && (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ color:'#aaa', fontSize:12 }}>
              Ingresa el número de factura donde se vendió la batería:
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && buscar()}
                placeholder="001-010-000042834"
                style={inputStyle}
              />
              <button onClick={buscar} disabled={buscando} style={{
                background:'#4361ee', border:'none', borderRadius:8,
                color:'#fff', padding:'8px 18px', fontSize:12,
                fontWeight:700, cursor:'pointer', whiteSpace:'nowrap',
              }}>
                {buscando ? '...' : '🔍 Buscar'}
              </button>
            </div>
            {error && <div style={{ color:'#e63946', fontSize:12 }}>{error}</div>}
          </div>
        )}

        {/* Paso 2 — Confirmar */}
        {paso === 2 && resultado && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {/* Info factura */}
            <div style={{ background:'#13131f', borderRadius:9,
              padding:'12px 14px', display:'flex', flexDirection:'column', gap:6 }}>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'#4cc9f0', fontFamily:'monospace',
                  fontSize:12 }}>{resultado.factura}</span>
                <span style={{ color:'#4caf50', fontFamily:'monospace',
                  fontWeight:700 }}>${fmt(resultado.total)}</span>
              </div>
              <div style={{ color:'#ccc', fontSize:13 }}>{resultado.cliente}</div>
              <div style={{ color:'#555', fontSize:11 }}>
                {fmtFecha(resultado.fecha_venta)} · {resultado.vendedor}
              </div>
            </div>

            {/* Ya registrada */}
            {resultado.ya_registrada && (
              <div style={{ background:'#2a1e00', borderRadius:8,
                padding:'8px 12px', color:'#ffc107', fontSize:12 }}>
                ⚠️ Esta factura ya tiene una garantía registrada
                (estado: {resultado.garantia_estado})
              </div>
            )}

            {/* Selector batería si hay más de 1 */}
            {resultado.baterias.length > 1 && (
              <div>
                <div style={{ color:'#aaa', fontSize:11,
                  marginBottom:6 }}>Selecciona la batería:</div>
                {resultado.baterias.map((b, i) => (
                  <div key={b.item_id} onClick={() => {
                    setBateriaIdx(i); setKmGarantia(b.km_garantia);
                  }} style={{
                    background: bateriaIdx === i ? '#001a2a' : '#13131f',
                    border: `1px solid ${bateriaIdx === i ? '#4cc9f0' : '#2a2a3e'}`,
                    borderRadius:7, padding:'8px 12px', cursor:'pointer',
                    marginBottom:4,
                  }}>
                    <div style={{ color:'#ccc', fontSize:12 }}>{b.descripcion}</div>
                    <div style={{ color:'#555', fontSize:10 }}>
                      {b.sublinea} · ${fmt(b.precio_venta)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Batería seleccionada */}
            {bat && resultado.baterias.length === 1 && (
              <div style={{ background:'#13131f', borderRadius:9,
                padding:'12px 14px' }}>
                <div style={{ color:'#4caf50', fontSize:11,
                  textTransform:'uppercase', letterSpacing:1,
                  marginBottom:4 }}>🔋 Batería</div>
                <div style={{ color:'#fff', fontSize:13,
                  fontWeight:600 }}>{bat.descripcion}</div>
                <div style={{ color:'#555', fontSize:11, marginTop:3 }}>
                  {bat.sublinea} · ${fmt(bat.precio_venta)}
                  · Garantía: {fmt(bat.km_garantia)} km
                </div>
              </div>
            )}

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => { setPaso(1); setResultado(null); }}
                style={btnCancelStyle}>← Volver</button>
              <button onClick={() => setPaso(3)} style={btnPrimaryStyle}>
                Continuar →
              </button>
            </div>
          </div>
        )}

        {/* Paso 3 — Km inicial */}
        {paso === 3 && resultado && bat && (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ background:'#13131f', borderRadius:9,
              padding:'10px 14px' }}>
              <div style={{ color:'#ccc', fontSize:12 }}>{bat.descripcion}</div>
              <div style={{ color:'#555', fontSize:11, marginTop:2 }}>
                {resultado.cliente} · {fmtFecha(resultado.fecha_venta)}
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div>
                <div style={{ color:'#aaa', fontSize:11, marginBottom:5 }}>
                  KM INICIAL AL VENDER
                  {resultado.km_obs && (
                    <span style={{ color:'#4cc9f0', marginLeft:6 }}>
                      (detectado: {fmt(resultado.km_obs)})
                    </span>
                  )}
                </div>
                <input
                  type="number"
                  value={kmInicial}
                  onChange={e => setKmInicial(e.target.value)}
                  placeholder="Ej: 45000"
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={{ color:'#aaa', fontSize:11, marginBottom:5 }}>
                  KM DE GARANTÍA
                </div>
                <input
                  type="number"
                  value={kmGarantia}
                  onChange={e => setKmGarantia(parseInt(e.target.value) || 40000)}
                  style={inputStyle}
                />
              </div>
            </div>

            <div>
              <div style={{ color:'#aaa', fontSize:11, marginBottom:5 }}>
                NOTAS (opcional)
              </div>
              <textarea value={notas} onChange={e => setNotas(e.target.value)}
                placeholder="Observaciones adicionales..."
                style={{ ...inputStyle, height:64, resize:'vertical' }} />
            </div>

            {resultado.obs_text && (
              <div style={{ background:'#13131f', borderRadius:7,
                padding:'8px 12px' }}>
                <div style={{ color:'#555', fontSize:10,
                  marginBottom:2 }}>OBSERVACIÓN ORIGINAL EN FACTURA:</div>
                <div style={{ color:'#888', fontSize:11,
                  fontFamily:'monospace' }}>{resultado.obs_text}</div>
              </div>
            )}

            {error && <div style={{ color:'#e63946', fontSize:12 }}>{error}</div>}

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={() => setPaso(2)} style={btnCancelStyle}>
                ← Volver
              </button>
              <button onClick={guardar} disabled={guardando}
                style={{ ...btnPrimaryStyle,
                  background: guardando ? '#2a2a3e' : '#4caf50',
                  borderColor: guardando ? '#2a2a3e' : '#4caf50',
                  color: guardando ? '#555' : '#fff' }}>
                {guardando ? 'Guardando...' : '✅ Registrar garantía'}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Modal Detalle / Gestión
// ═══════════════════════════════════════════════════════════════════════════════
function ModalDetalle({ garantiaId, onClose, onRefresh }) {
  const [data,       setData]       = useState(null);
  const [tabDet,     setTabDet]     = useState('info'); // info|checkpoints|proceso
  const [guardando,  setGuardando]  = useState(false);

  // Form checkpoint
  const [cpNumero,   setCpNumero]   = useState(1);
  const [cpFecha,    setCpFecha]    = useState(new Date().toISOString().slice(0,10));
  const [cpKm,       setCpKm]       = useState('');
  const [cpObs,      setCpObs]      = useState('');

  // Form proceso
  const [procFechaRec,  setProcFechaRec]  = useState('');
  const [procKmReclamo, setProcKmReclamo] = useState('');
  const [procMotivo,    setProcMotivo]    = useState('');
  const [procFechaEnv,  setProcFechaEnv]  = useState('');
  const [procDiasLim,   setProcDiasLim]   = useState(15);
  const [procResultado, setProcResultado] = useState('');
  const [procFechaRes,  setProcFechaRes]  = useState('');
  const [procMotivoNeg, setProcMotivoNeg] = useState('');
  const [procFechaRep,  setProcFechaRep]  = useState('');
  const [procNumIng,    setProcNumIng]    = useState('');

  const cargar = useCallback(() => {
    api.get(`/garantias/baterias/${garantiaId}`).then(d => {
      setData(d);
      // Pre-llenar proceso si existe
      if (d.proceso) {
        const p = d.proceso;
        if (p.fecha_recepcion) setProcFechaRec(p.fecha_recepcion);
        if (p.km_al_reclamo)   setProcKmReclamo(String(p.km_al_reclamo));
        if (p.motivo_falla)    setProcMotivo(p.motivo_falla);
        if (p.fecha_envio_proveedor) setProcFechaEnv(p.fecha_envio_proveedor);
        if (p.dias_limite)     setProcDiasLim(p.dias_limite);
        if (p.resultado)       setProcResultado(p.resultado);
        if (p.fecha_resultado) setProcFechaRes(p.fecha_resultado);
        if (p.motivo_negacion) setProcMotivoNeg(p.motivo_negacion);
        if (p.fecha_reposicion)setProcFechaRep(p.fecha_reposicion);
        if (p.numero_ingreso)  setProcNumIng(p.numero_ingreso);
      }
      // Siguiente checkpoint disponible
      const cpsTomados = (d.checkpoints || []).map(c => c.numero);
      const siguiente = [1,2,3].find(n => !cpsTomados.includes(n)) || 3;
      setCpNumero(siguiente);
    });
  }, [garantiaId]);

  useEffect(() => { cargar(); }, [cargar]);

  async function guardarCheckpoint() {
    if (!cpKm) return;
    setGuardando(true);
    await api.post(`/garantias/baterias/${garantiaId}/checkpoint`, {
      numero: cpNumero, fecha: cpFecha,
      km_actual: parseInt(cpKm), observacion: cpObs,
      registrado_por: 'panel',
    });
    cargar(); onRefresh();
    setCpKm(''); setCpObs('');
    setGuardando(false);
  }

  async function guardarProceso() {
    setGuardando(true);
    await api.post(`/garantias/baterias/${garantiaId}/proceso`, {
      fecha_recepcion:     procFechaRec  || null,
      km_al_reclamo:       procKmReclamo ? parseInt(procKmReclamo) : null,
      motivo_falla:        procMotivo    || null,
      fecha_envio_proveedor: procFechaEnv || null,
      dias_limite_respuesta: procDiasLim,
      resultado:           procResultado || null,
      fecha_resultado:     procFechaRes  || null,
      motivo_negacion:     procMotivoNeg || null,
      fecha_reposicion_inv:procFechaRep  || null,
      numero_ingreso:      procNumIng    || null,
      registrado_por:      'panel',
    });
    cargar(); onRefresh();
    setGuardando(false);
  }

  if (!data) return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0,
        background:'rgba(0,0,0,0.65)', zIndex:200 }} />
      <div style={{ position:'fixed', top:'50%', left:'50%',
        transform:'translate(-50%,-50%)', background:'#1e1e2e',
        borderRadius:14, padding:40, zIndex:201, color:'#555' }}>
        Cargando...
      </div>
    </>
  );

  const g   = data.garantia;
  const cps = data.checkpoints || [];
  const sem = SEMAFORO[g.color] || SEMAFORO.verde;
  const est = ESTADOS[g.estado] || ESTADOS.activa;

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0,
        background:'rgba(0,0,0,0.65)', zIndex:200 }} />
      <div style={{
        position:'fixed', top:'50%', left:'50%',
        transform:'translate(-50%,-50%)',
        background:'#1e1e2e', borderRadius:14,
        width:600, maxHeight:'88vh',
        zIndex:201, border:'1px solid #2a2a3e',
        boxShadow:'0 16px 48px rgba(0,0,0,0.6)',
        display:'flex', flexDirection:'column',
        overflow:'hidden',
      }}>
        {/* Header */}
        <div style={{ padding:'20px 24px 0',
          borderBottom:'1px solid #2a2a3e' }}>
          <div style={{ display:'flex', justifyContent:'space-between',
            alignItems:'flex-start', marginBottom:12 }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:20 }}>{sem.icon}</span>
                <span style={{ color:'#fff', fontWeight:700,
                  fontSize:15 }}>{g.descripcion}</span>
                <BadgeEstado estado={g.estado} />
              </div>
              <div style={{ color:'#4cc9f0', fontFamily:'monospace',
                fontSize:12, marginTop:4 }}>{g.factura}</div>
              <div style={{ color:'#ccc', fontSize:13, marginTop:2 }}>
                {g.nombre_cliente}
                <span style={{ color:'#555', marginLeft:8,
                  fontSize:11 }}>{g.cedula}</span>
              </div>
            </div>
            <button onClick={onClose} style={{ background:'none',
              border:'none', color:'#555', fontSize:20,
              cursor:'pointer', padding:4 }}>✕</button>
          </div>

          {/* KPIs rápidos */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)',
            gap:8, marginBottom:14 }}>
            {[
              ['Venta',    fmtFecha(g.fecha_venta), '#aaa'],
              ['Km inicial', g.km_inicial ? fmt(g.km_inicial) : '—', '#4cc9f0'],
              ['Km garantía',fmt(g.km_garantia), '#ffc107'],
              ['Km estimado',fmt(g.km_estimado), sem.color],
            ].map(([l, v, c]) => (
              <div key={l} style={{ background:'#13131f', borderRadius:7,
                padding:'8px 10px', textAlign:'center' }}>
                <div style={{ color:'#555', fontSize:9,
                  textTransform:'uppercase' }}>{l}</div>
                <div style={{ color:c, fontFamily:'monospace',
                  fontWeight:700, fontSize:13, marginTop:2 }}>{v}</div>
              </div>
            ))}
          </div>
          <SemaforoBarra pct_tiempo={g.pct_tiempo} pct_km={g.pct_km} color={sem.color} />

          {/* Tabs */}
          <div style={{ display:'flex', gap:0, marginTop:14 }}>
            {[
              { key:'info',        label:'📋 Info' },
              { key:'checkpoints', label:`🔍 Seguimiento (${cps.length}/3)` },
              { key:'proceso',     label:'⚙️ Proceso garantía' },
            ].map(t => (
              <button key={t.key} onClick={() => setTabDet(t.key)} style={{
                background:'none', border:'none',
                borderBottom:`2px solid ${tabDet===t.key ? '#4361ee' : 'transparent'}`,
                color: tabDet===t.key ? '#4361ee' : '#555',
                fontWeight: tabDet===t.key ? 700 : 400,
                fontSize:12, padding:'8px 16px', cursor:'pointer',
              }}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Contenido scrolleable */}
        <div style={{ flex:1, overflowY:'auto', padding:'16px 24px' }}>

          {/* Tab Info */}
          {tabDet === 'info' && (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {[
                ['Vendedor',    g.vendedor || '—'],
                ['Días activa', `${g.dias_activa} días`],
                ['Días restantes garantía', diasLabel(g.dias_restantes)],
                ['% Tiempo usado', `${g.pct_tiempo}%`],
                ['% Km usado (estimado)', `${g.pct_km}%`],
                ['Checkpoints registrados', `${cps.length} de 3`],
                ['Notas', g.notas || '—'],
              ].map(([k, v]) => (
                <div key={k} style={{ display:'flex',
                  justifyContent:'space-between',
                  padding:'8px 0', borderBottom:'1px solid #1a1a2e' }}>
                  <span style={{ color:'#555', fontSize:12 }}>{k}</span>
                  <span style={{ color:'#ccc', fontSize:12,
                    textAlign:'right', maxWidth:280 }}>{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tab Checkpoints */}
          {tabDet === 'checkpoints' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {/* Checkpoints existentes */}
              {cps.length === 0 ? (
                <div style={{ color:'#555', textAlign:'center',
                  padding:20, fontSize:12 }}>Sin checkpoints registrados</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {cps.map(c => (
                    <div key={c.numero} style={{
                      background:'#13131f', borderRadius:8,
                      padding:'12px 14px',
                      borderLeft:`3px solid #4cc9f0`,
                    }}>
                      <div style={{ display:'flex',
                        justifyContent:'space-between',
                        alignItems:'center' }}>
                        <span style={{ color:'#4cc9f0', fontWeight:700,
                          fontSize:13 }}>Checkpoint #{c.numero}</span>
                        <span style={{ color:'#aaa', fontSize:11 }}>
                          {fmtFecha(c.fecha)}
                        </span>
                      </div>
                      <div style={{ display:'flex', gap:16, marginTop:6 }}>
                        <span style={{ color:'#ffc107', fontFamily:'monospace',
                          fontSize:12 }}>Km: {fmt(c.km_actual)}</span>
                        <span style={{ color:'#4caf50', fontFamily:'monospace',
                          fontSize:12 }}>
                          +{fmt(c.km_recorrido)} km recorridos
                        </span>
                      </div>
                      {c.observacion && (
                        <div style={{ color:'#888', fontSize:11,
                          marginTop:4 }}>{c.observacion}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Agregar nuevo checkpoint */}
              {cps.length < 3 && g.estado === 'activa' && (
                <div style={{ background:'#13131f', borderRadius:10,
                  padding:'14px 16px',
                  border:'1px dashed #2a2a3e' }}>
                  <div style={{ color:'#aaa', fontWeight:600, fontSize:13,
                    marginBottom:12 }}>
                    Registrar Checkpoint #{cpNumero}
                  </div>
                  <div style={{ display:'grid',
                    gridTemplateColumns:'1fr 1fr', gap:8,
                    marginBottom:8 }}>
                    <div>
                      <div style={labelStyle}>Fecha</div>
                      <input type="date" value={cpFecha}
                        onChange={e => setCpFecha(e.target.value)}
                        style={inputStyle} />
                    </div>
                    <div>
                      <div style={labelStyle}>Km actual</div>
                      <input type="number" value={cpKm}
                        onChange={e => setCpKm(e.target.value)}
                        placeholder="Ej: 58000"
                        style={inputStyle} />
                    </div>
                  </div>
                  <div style={{ marginBottom:10 }}>
                    <div style={labelStyle}>Observación</div>
                    <input value={cpObs} onChange={e => setCpObs(e.target.value)}
                      placeholder="Estado de la batería, condiciones..."
                      style={inputStyle} />
                  </div>
                  <button onClick={guardarCheckpoint} disabled={!cpKm || guardando}
                    style={{ ...btnPrimaryStyle, width:'100%',
                      opacity: cpKm ? 1 : 0.5 }}>
                    {guardando ? 'Guardando...' : '✅ Guardar checkpoint'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab Proceso */}
          {tabDet === 'proceso' && (
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ color:'#aaa', fontSize:12 }}>
                Registra el proceso cuando el cliente regresa con batería fallada.
              </div>

              {/* Recepción */}
              <div style={{ background:'#13131f', borderRadius:9,
                padding:'12px 14px' }}>
                <div style={{ color:'#e63946', fontWeight:600, fontSize:12,
                  marginBottom:10 }}>📥 Recepción de batería dañada</div>
                <div style={{ display:'grid',
                  gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <div>
                    <div style={labelStyle}>Fecha recepción</div>
                    <input type="date" value={procFechaRec}
                      onChange={e => setProcFechaRec(e.target.value)}
                      style={inputStyle} />
                  </div>
                  <div>
                    <div style={labelStyle}>Km al reclamo</div>
                    <input type="number" value={procKmReclamo}
                      onChange={e => setProcKmReclamo(e.target.value)}
                      placeholder="Km al momento del reclamo"
                      style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginTop:8 }}>
                  <div style={labelStyle}>Motivo de falla</div>
                  <input value={procMotivo}
                    onChange={e => setProcMotivo(e.target.value)}
                    placeholder="Descripción de la falla..."
                    style={inputStyle} />
                </div>
              </div>

              {/* Envío proveedor */}
              <div style={{ background:'#13131f', borderRadius:9,
                padding:'12px 14px' }}>
                <div style={{ color:'#ffc107', fontWeight:600, fontSize:12,
                  marginBottom:10 }}>📤 Envío al proveedor</div>
                <div style={{ display:'grid',
                  gridTemplateColumns:'1fr 1fr', gap:8 }}>
                  <div>
                    <div style={labelStyle}>Fecha envío</div>
                    <input type="date" value={procFechaEnv}
                      onChange={e => setProcFechaEnv(e.target.value)}
                      style={inputStyle} />
                  </div>
                  <div>
                    <div style={labelStyle}>Días límite respuesta</div>
                    <input type="number" value={procDiasLim}
                      onChange={e => setProcDiasLim(parseInt(e.target.value)||15)}
                      style={inputStyle} />
                  </div>
                </div>
              </div>

              {/* Resultado */}
              <div style={{ background:'#13131f', borderRadius:9,
                padding:'12px 14px' }}>
                <div style={{ color:'#4caf50', fontWeight:600, fontSize:12,
                  marginBottom:10 }}>✅ Resultado</div>
                <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                  {['aprobada','negada'].map(r => (
                    <button key={r} onClick={() => setProcResultado(r)} style={{
                      flex:1, padding:'8px 0', borderRadius:7,
                      cursor:'pointer', fontWeight:700, fontSize:12,
                      background: procResultado===r
                        ? (r==='aprobada' ? '#0d2218' : '#2a0d0d')
                        : '#1a1a2a',
                      border: `1px solid ${procResultado===r
                        ? (r==='aprobada' ? '#4caf50' : '#e63946')
                        : '#2a2a3e'}`,
                      color: procResultado===r
                        ? (r==='aprobada' ? '#4caf50' : '#e63946')
                        : '#555',
                    }}>
                      {r === 'aprobada' ? '✅ Aprobada' : '❌ Negada'}
                    </button>
                  ))}
                </div>
                {procResultado === 'negada' && (
                  <div style={{ marginBottom:8 }}>
                    <div style={labelStyle}>Motivo negación</div>
                    <input value={procMotivoNeg}
                      onChange={e => setProcMotivoNeg(e.target.value)}
                      placeholder="Razón del rechazo del proveedor..."
                      style={inputStyle} />
                  </div>
                )}
                {procResultado === 'aprobada' && (
                  <div style={{ display:'grid',
                    gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    <div>
                      <div style={labelStyle}>Fecha reposición inv.</div>
                      <input type="date" value={procFechaRep}
                        onChange={e => setProcFechaRep(e.target.value)}
                        style={inputStyle} />
                    </div>
                    <div>
                      <div style={labelStyle}>N° ingreso Morpheus</div>
                      <input value={procNumIng}
                        onChange={e => setProcNumIng(e.target.value)}
                        placeholder="Ref. ingreso inventario"
                        style={inputStyle} />
                    </div>
                  </div>
                )}
                {procResultado && (
                  <div style={{ marginTop:8 }}>
                    <div style={labelStyle}>Fecha resultado</div>
                    <input type="date" value={procFechaRes}
                      onChange={e => setProcFechaRes(e.target.value)}
                      style={inputStyle} />
                  </div>
                )}
              </div>

              <button onClick={guardarProceso} disabled={guardando}
                style={{ ...btnPrimaryStyle, width:'100%',
                  background:'#4361ee', borderColor:'#4361ee' }}>
                {guardando ? 'Guardando...' : '💾 Guardar proceso'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Componente principal
// ═══════════════════════════════════════════════════════════════════════════════
export default function GarantiasModule() {
  const [data,          setData]          = useState(null);
  const [cargando,      setCargando]      = useState(true);
  const [estadoFiltro,  setEstadoFiltro]  = useState('activa');
  const [alertas,       setAlertas]       = useState(null);
  const [tabMain,       setTabMain]       = useState('lista'); // lista|alertas|buscador
  const [modalReg,      setModalReg]      = useState(false);
  const [modalDetId,    setModalDetId]    = useState(null);
  const [busqueda,      setBusqueda]      = useState('');

  const cargar = useCallback(() => {
    setCargando(true);
    api.get(`/garantias/baterias?estado=${estadoFiltro}`)
      .then(d => { setData(d); setCargando(false); })
      .catch(() => setCargando(false));
  }, [estadoFiltro]);

  const cargarAlertas = useCallback(() => {
    api.get('/garantias/alertas').then(setAlertas);
  }, []);

  useEffect(() => { cargar(); cargarAlertas(); }, [cargar, cargarAlertas]);

  const garantias = (data?.garantias || []).filter(g =>
    !busqueda || g.nombre_cliente.toLowerCase().includes(busqueda.toLowerCase())
      || g.factura.includes(busqueda)
      || g.cedula.includes(busqueda)
  );

  const kpis = data?.kpis || {};
  const totalAlertas = (alertas?.total_alertas || 0);

  return (
    <div style={{ fontFamily:"'Segoe UI', sans-serif", color:'#e0e0e0' }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'flex-start', marginBottom:24 }}>
        <div>
          <h2 style={{ color:'#fff', margin:0, fontSize:22, fontWeight:700 }}>
            🔋 Garantías de Baterías
          </h2>
          <div style={{ color:'#555', fontSize:13, marginTop:3 }}>
            ENERGEIZER · Control de proceso · 2 años / 40,000 km
          </div>
        </div>
        <button onClick={() => setModalReg(true)} style={{
          background:'#4cc9f0', border:'none', color:'#000',
          borderRadius:8, padding:'9px 20px', fontSize:13,
          fontWeight:700, cursor:'pointer',
        }}>+ Registrar garantía</button>
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)',
        gap:10, marginBottom:24 }}>
        <KPICard label="Activas"    value={kpis.activas    || 0} color="#4cc9f0" icon="🔋" />
        <KPICard label="En proceso" value={kpis.en_proceso || 0} color="#ffc107" icon="⚙️" />
        <KPICard label="Repuestas"  value={kpis.repuestas  || 0} color="#4caf50" icon="✅" />
        <KPICard label="Negadas"    value={kpis.negadas    || 0} color="#e63946" icon="❌" />
        <KPICard
          label="Tasa aprobación"
          value={kpis.tasa_aprobacion !== null
            ? `${kpis.tasa_aprobacion}%` : '—'}
          color="#7209b7" icon="📊"
          sub={`${(kpis.repuestas||0)+(kpis.negadas||0)} procesadas`}
        />
      </div>

      {/* Tabs principales */}
      <div style={{ display:'flex', gap:4, marginBottom:16,
        borderBottom:'1px solid #2a2a3e', paddingBottom:0 }}>
        {[
          { key:'lista',    label:'📋 Garantías' },
          { key:'alertas',  label:`🚨 Alertas ${totalAlertas > 0 ? `(${totalAlertas})` : ''}`,
            color: totalAlertas > 0 ? '#e63946' : undefined },
          { key:'buscador', label:'🔍 Buscador Yuasa', color: '#4caf50' },
        ].map(t => (
          <button key={t.key} onClick={() => setTabMain(t.key)} style={{
            background:'none', border:'none',
            borderBottom:`2px solid ${tabMain===t.key ? '#4361ee' : 'transparent'}`,
            color: t.color || (tabMain===t.key ? '#4361ee' : '#555'),
            fontWeight: tabMain===t.key ? 700 : 400,
            fontSize:13, padding:'8px 16px', cursor:'pointer',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Tab Lista */}
      {tabMain === 'lista' && (
        <>
          {/* Controles */}
          <div style={{ display:'flex', gap:8, marginBottom:14,
            alignItems:'center', flexWrap:'wrap' }}>
            <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar cliente, factura, cédula..."
              style={{ ...inputStyle, flex:1, maxWidth:280 }} />

            {['activa','en_proceso','repuesta','negada','vencida','todas'].map(e => (
              <button key={e} onClick={() => setEstadoFiltro(e)} style={{
                background: estadoFiltro===e
                  ? `${ESTADOS[e]?.color || '#fff'}22` : '#1e1e2e',
                border: `1px solid ${estadoFiltro===e
                  ? ESTADOS[e]?.color || '#fff' : '#2a2a3e'}`,
                color: estadoFiltro===e
                  ? ESTADOS[e]?.color || '#fff' : '#555',
                borderRadius:7, padding:'5px 12px',
                fontSize:11, fontWeight:600, cursor:'pointer',
              }}>
                {e === 'todas' ? 'Todas' : ESTADOS[e]?.label || e}
              </button>
            ))}
          </div>

          {/* Tabla */}
          {cargando ? (
            <div style={{ color:'#555', textAlign:'center', padding:40 }}>
              Cargando...
            </div>
          ) : garantias.length === 0 ? (
            <div style={{ color:'#555', textAlign:'center', padding:40 }}>
              Sin garantías {estadoFiltro !== 'todas' ? `con estado "${ESTADOS[estadoFiltro]?.label}"` : ''}
            </div>
          ) : (
            <div style={{ background:'#1e1e2e', borderRadius:12,
              overflow:'hidden' }}>
              {/* Header tabla */}
              <div style={{ display:'grid',
                gridTemplateColumns:'40px 1fr 200px 110px 110px 120px 80px',
                padding:'9px 16px', background:'#13131f',
                color:'#555', fontSize:10, fontWeight:700,
                textTransform:'uppercase', letterSpacing:0.5, gap:8 }}>
                <span></span>
                <span>Batería / Cliente</span>
                <span>Factura</span>
                <span style={{textAlign:'right'}}>Km inicial</span>
                <span style={{textAlign:'right'}}>Km estimado</span>
                <span style={{textAlign:'center'}}>Progreso</span>
                <span style={{textAlign:'center'}}>Estado</span>
              </div>

              {garantias.map((g, i) => {
                const sem = SEMAFORO[g.color] || SEMAFORO.verde;
                return (
                  <div key={g.id}
                    onClick={() => setModalDetId(g.id)}
                    style={{
                      display:'grid',
                      gridTemplateColumns:'40px 1fr 200px 110px 110px 120px 80px',
                      padding:'13px 16px', gap:8, alignItems:'center',
                      background: i%2===0 ? '#1e1e2e' : '#1a1a2e',
                      borderBottom:'1px solid #13131f',
                      cursor:'pointer', transition:'background .15s',
                    }}>
                    {/* Semáforo */}
                    <span style={{ textAlign:'center', fontSize:16 }}>
                      {sem.icon}
                    </span>

                    {/* Batería + cliente */}
                    <div>
                      <div style={{ color:'#fff', fontSize:13,
                        fontWeight:600, whiteSpace:'nowrap',
                        overflow:'hidden', textOverflow:'ellipsis',
                        maxWidth:260 }}>
                        {g.descripcion}
                      </div>
                      <div style={{ color:'#aaa', fontSize:11,
                        marginTop:2, whiteSpace:'nowrap',
                        overflow:'hidden', textOverflow:'ellipsis',
                        maxWidth:260 }}>
                        {g.nombre_cliente}
                      </div>
                      <div style={{ color:'#555', fontSize:10 }}>
                        {fmtFecha(g.fecha_venta)}
                        {g.num_checkpoints > 0 && (
                          <span style={{ marginLeft:8, color:'#4cc9f0' }}>
                            ✓ {g.num_checkpoints} cp
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Factura */}
                    <div style={{ color:'#4cc9f0', fontFamily:'monospace',
                      fontSize:11 }}>{g.factura}</div>

                    {/* Km inicial */}
                    <div style={{ textAlign:'right', color:'#aaa',
                      fontFamily:'monospace', fontSize:12 }}>
                      {g.km_inicial ? fmt(g.km_inicial) : '—'}
                    </div>

                    {/* Km estimado */}
                    <div style={{ textAlign:'right', color:sem.color,
                      fontFamily:'monospace', fontSize:12, fontWeight:600 }}>
                      {fmt(g.km_estimado)}
                    </div>

                    {/* Barra progreso */}
                    <div>
                      <SemaforoBarra pct_tiempo={g.pct_tiempo}
                        pct_km={g.pct_km} color={sem.color} />
                      <div style={{ color:'#555', fontSize:9,
                        marginTop:3, textAlign:'center' }}>
                        {g.dias_restantes > 0
                          ? `${g.dias_restantes}d restantes`
                          : 'Vencida'}
                      </div>
                    </div>

                    {/* Estado */}
                    <div style={{ textAlign:'center' }}>
                      <BadgeEstado estado={g.estado} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Tab Alertas */}
      {tabMain === 'alertas' && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Sin checkpoint */}
          {(alertas?.sin_checkpoint || []).length > 0 && (
            <div style={{ background:'#1e1e2e', borderRadius:12,
              padding:'16px 20px' }}>
              <div style={{ color:'#ffc107', fontWeight:700, fontSize:14,
                marginBottom:12 }}>
                ⏰ Sin checkpoint en más de 6 meses
                ({alertas.sin_checkpoint.length})
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {alertas.sin_checkpoint.map(a => (
                  <div key={a.id}
                    onClick={() => setModalDetId(a.id)}
                    style={{ display:'flex', justifyContent:'space-between',
                      alignItems:'center', background:'#13131f',
                      borderRadius:8, padding:'10px 14px',
                      borderLeft:'3px solid #ffc107', cursor:'pointer' }}>
                    <div>
                      <div style={{ color:'#fff', fontSize:13 }}>
                        {a.cliente}
                      </div>
                      <div style={{ color:'#555', fontSize:11, marginTop:2 }}>
                        {a.descripcion} · {a.factura}
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ color:'#ffc107', fontSize:11 }}>
                        Desde {fmtFecha(a.fecha_venta)}
                      </div>
                      <div style={{ color:'#555', fontSize:10 }}>
                        Último: {a.ultimo_checkpoint
                          ? fmtFecha(a.ultimo_checkpoint) : 'Nunca'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Procesos vencidos */}
          {(alertas?.procesos_vencidos || []).length > 0 && (
            <div style={{ background:'#1e1e2e', borderRadius:12,
              padding:'16px 20px' }}>
              <div style={{ color:'#e63946', fontWeight:700, fontSize:14,
                marginBottom:12 }}>
                🚨 Procesos sin respuesta del proveedor
                ({alertas.procesos_vencidos.length})
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {alertas.procesos_vencidos.map(a => (
                  <div key={a.id}
                    onClick={() => setModalDetId(a.id)}
                    style={{ display:'flex', justifyContent:'space-between',
                      alignItems:'center', background:'#13131f',
                      borderRadius:8, padding:'10px 14px',
                      borderLeft:'3px solid #e63946', cursor:'pointer' }}>
                    <div>
                      <div style={{ color:'#fff', fontSize:13 }}>
                        {a.cliente}
                      </div>
                      <div style={{ color:'#555', fontSize:11, marginTop:2 }}>
                        {a.descripcion} · Enviado: {fmtFecha(a.fecha_envio)}
                      </div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ color:'#e63946', fontSize:12,
                        fontWeight:700 }}>
                        {a.dias_vencido} días vencido
                      </div>
                      <div style={{ color:'#555', fontSize:10 }}>
                        Límite: {fmtFecha(a.fecha_limite)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {totalAlertas === 0 && (
            <div style={{ color:'#4caf50', textAlign:'center',
              padding:40, fontSize:14 }}>
              ✅ Sin alertas activas
            </div>
          )}
        </div>
      )}

      {/* Tab Buscador Yuasa */}
      {tabMain === 'buscador' && (
        <TabBuscadorBaterias />
      )}

      {/* Modales */}
      {modalReg && (
        <ModalRegistrar
          onClose={() => setModalReg(false)}
          onGuardado={() => { cargar(); cargarAlertas(); }}
        />
      )}
      {modalDetId && (
        <ModalDetalle
          garantiaId={modalDetId}
          onClose={() => setModalDetId(null)}
          onRefresh={() => { cargar(); cargarAlertas(); }}
        />
      )}
    </div>
  );
}

// ─── Estilos compartidos ──────────────────────────────────────────────────────
const inputStyle = {
  background:'#0a0a14', border:'1px solid #2a2a3e',
  borderRadius:7, color:'#fff', padding:'8px 10px',
  fontSize:12, outline:'none', width:'100%',
  boxSizing:'border-box',
};
const labelStyle = {
  color:'#555', fontSize:10, textTransform:'uppercase',
  letterSpacing:0.5, marginBottom:4,
};
const btnPrimaryStyle = {
  background:'#4361ee', border:'1px solid #4361ee',
  borderRadius:7, color:'#fff', padding:'8px 20px',
  fontSize:12, fontWeight:700, cursor:'pointer',
};
const btnCancelStyle = {
  background:'none', border:'1px solid #2a2a3e',
  borderRadius:7, color:'#aaa', padding:'8px 18px',
  fontSize:12, cursor:'pointer',
};